import React, { useState, useEffect } from 'react';
import { Swords, X, ThumbsUp, Trophy, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DanceBattle = ({ isOpen, onClose, user, isGuest, players, socketRef }) => {
  const [battles, setBattles] = useState([]);
  const [challenging, setChallenging] = useState(false);

  useEffect(() => {
    if (isOpen) fetchBattles();
  }, [isOpen]);

  useEffect(() => {
    if (socketRef?.current) {
      const handlers = ['dance_battle_challenge', 'dance_battle_update', 'dance_battle_vote', 'dance_battle_ended'];
      handlers.forEach(evt => socketRef.current.on(evt, () => fetchBattles()));
      return () => handlers.forEach(evt => socketRef.current.off(evt));
    }
  }, [socketRef]);

  const fetchBattles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/nightclub/dance-battle/active`);
      if (res.ok) setBattles(await res.json());
    } catch (e) { /* silent */ }
  };

  const challengePlayer = async (opponentId, opponentName) => {
    if (isGuest) { toast.error('Sign in to battle'); return; }
    setChallenging(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/dance-battle/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ opponent_id: opponentId, opponent_name: opponentName })
      });
      if (res.ok) {
        toast.success(`Challenge sent to ${opponentName}!`);
        fetchBattles();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to challenge');
      }
    } catch (e) {
      toast.error('Failed to send challenge');
    } finally {
      setChallenging(false);
    }
  };

  const respondToBattle = async (battleId, accept) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/nightclub/dance-battle/${battleId}/respond`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accept })
      });
      fetchBattles();
    } catch (e) {
      toast.error('Failed to respond');
    }
  };

  const voteBattle = async (battleId, voteFor) => {
    if (isGuest) { toast.error('Sign in to vote'); return; }
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/dance-battle/${battleId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ vote_for: voteFor })
      });
      if (res.ok) {
        toast.success('Vote cast!');
        fetchBattles();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to vote');
      }
    } catch (e) {
      toast.error('Failed to vote');
    }
  };

  const endBattle = async (battleId) => {
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/nightclub/dance-battle/${battleId}/end`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBattles();
    } catch (e) {
      toast.error('Failed to end battle');
    }
  };

  if (!isOpen) return null;

  // Filter nearby players (exclude self and guests)
  const otherPlayers = Object.values(players || {}).filter(p => 
    p.id !== user?.user_id && p.name !== 'Guest' && !p.id?.startsWith('guest_')
  );

  return (
    <div className="absolute bottom-20 left-4 z-40 w-72" data-testid="dance-battle-panel">
      <div className="glass-heavy rounded-xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Swords className="w-4 h-4 text-red-400" />
            <span className="font-semibold text-sm">Dance Battles</span>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1">
            <X size={16} />
          </button>
        </div>

        {/* Active Battles */}
        {battles.length > 0 && (
          <div className="border-b border-white/10">
            {battles.map(battle => (
              <div key={battle.battle_id} className="p-3 border-b border-white/5 last:border-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-bold uppercase ${
                      battle.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                      battle.status === 'active' || battle.status === 'voting' ? 'bg-red-500/20 text-red-300' :
                      'bg-green-500/20 text-green-300'
                    }`}>
                      {battle.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  {/* Challenger */}
                  <div className="flex-1 text-center">
                    <p className="text-xs font-bold truncate">{battle.challenger_name}</p>
                    {(battle.status === 'active' || battle.status === 'voting') && (
                      <>
                        <p className="text-lg font-bold text-violet-400">{battle.votes?.challenger || 0}</p>
                        <Button size="sm" onClick={() => voteBattle(battle.battle_id, 'challenger')}
                          className="h-6 text-[10px] bg-violet-600/50 hover:bg-violet-600 w-full mt-1"
                          disabled={battle.voted_by?.includes(user?.user_id)}>
                          <ThumbsUp size={10} className="mr-1" /> Vote
                        </Button>
                      </>
                    )}
                  </div>

                  <span className="text-white/30 font-bold text-xs">VS</span>

                  {/* Opponent */}
                  <div className="flex-1 text-center">
                    <p className="text-xs font-bold truncate">{battle.opponent_name}</p>
                    {(battle.status === 'active' || battle.status === 'voting') && (
                      <>
                        <p className="text-lg font-bold text-fuchsia-400">{battle.votes?.opponent || 0}</p>
                        <Button size="sm" onClick={() => voteBattle(battle.battle_id, 'opponent')}
                          className="h-6 text-[10px] bg-fuchsia-600/50 hover:bg-fuchsia-600 w-full mt-1"
                          disabled={battle.voted_by?.includes(user?.user_id)}>
                          <ThumbsUp size={10} className="mr-1" /> Vote
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Pending - Accept/Decline */}
                {battle.status === 'pending' && battle.opponent_id === user?.user_id && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => respondToBattle(battle.battle_id, true)}
                      className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-500">
                      Accept
                    </Button>
                    <Button size="sm" onClick={() => respondToBattle(battle.battle_id, false)}
                      className="flex-1 h-7 text-xs bg-red-600 hover:bg-red-500" variant="destructive">
                      Decline
                    </Button>
                  </div>
                )}

                {/* End Battle (for challenger/admin) */}
                {(battle.status === 'active' || battle.status === 'voting') && 
                  (battle.challenger_id === user?.user_id) && (
                  <Button size="sm" onClick={() => endBattle(battle.battle_id)}
                    className="w-full mt-2 h-7 text-xs bg-yellow-600 hover:bg-yellow-500">
                    <Trophy size={12} className="mr-1" /> End Battle
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Challenge a Player */}
        {!isGuest && otherPlayers.length > 0 && (
          <div className="p-3">
            <p className="text-xs text-white/50 mb-2">Challenge a player nearby:</p>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {otherPlayers.slice(0, 8).map(player => (
                <div key={player.id} className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5">
                  <span className="text-xs font-medium truncate flex-1">{player.name}</span>
                  <Button 
                    size="sm" onClick={() => challengePlayer(player.id, player.name)}
                    disabled={challenging}
                    className="h-6 text-[10px] bg-red-600/50 hover:bg-red-600 ml-2"
                    data-testid={`challenge-${player.id}`}
                  >
                    <Swords size={10} className="mr-1" /> Battle
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isGuest && otherPlayers.length === 0 && battles.length === 0 && (
          <p className="text-white/40 text-xs text-center py-4">No players nearby to challenge</p>
        )}
        {isGuest && (
          <p className="text-white/40 text-xs text-center py-4">Sign in to participate in dance battles</p>
        )}
      </div>
    </div>
  );
};

export default DanceBattle;
